import { useTranslation } from '@rbx/intl';
import { Typography } from '@rbx/ui';
import React, { Fragment, FunctionComponent } from 'react';
import useHeadingGroupStyles from '@modules/ui/shared/styles/HeadingGroup.styles';
import {
  ReferenceType,
  APIReferenceProperty,
  APIReferenceConstructor,
  APIReferenceMethod,
  APIReferenceFunction,
  APIReferenceMathOperation,
} from '@modules/client';
import {
  constructorsID,
  functionsID,
  methodsID,
  propertiesID,
  mathOperationsID,
} from '../../constants/apiReferenceConstants';
import APIReferenceDescription from '../../sharedUI/components/APIReferenceDescription';
import { DatatypeReference } from '../../types';
import DatatypeCharacteristicsIterator from './DatatypeCharacteristicIterator';
import ReferenceSummary from '../../sharedUI/components/ReferenceSummary';
import ReferenceSummaryV2 from '../../sharedUI/components/ReferenceSummaryV2';
import DatatypeMemberDetailsContainer from './DatatypeMemberDetailsContainer';

export interface DatatypeContentProps {
  datatypeReference: DatatypeReference;
  engineRevampEnabled?: boolean;
}

const DatatypeContent: FunctionComponent<React.PropsWithChildren<DatatypeContentProps>> = ({
  datatypeReference,
  engineRevampEnabled = false,
}) => {
  const {
    name,
    description,
    codeSamples,
    properties,
    functions,
    constructors,
    methods,
    mathOperations,
  } = datatypeReference;
  const { translate } = useTranslation();

  const {
    classes: { root },
  } = useHeadingGroupStyles();

  const hasConstructors = constructors && constructors.length > 0;
  const hasProperties = properties && properties.length > 0;
  const hasMethods = methods && methods.length > 0;
  const hasFunctions = functions && functions.length > 0;
  const hasMathOperations = mathOperations && mathOperations.length > 0;

  if (engineRevampEnabled) {
    return (
      <div className={root}>
        <ReferenceSummaryV2 referenceType={ReferenceType.DATATYPE} reference={datatypeReference} />
        <APIReferenceDescription
          name={name}
          description={description}
          codeSamples={codeSamples}
          engineRevampEnabled
        />
        <DatatypeMemberDetailsContainer
          constructors={constructors as APIReferenceConstructor[] | null}
          properties={properties as APIReferenceProperty[] | null}
          methods={methods as APIReferenceMethod[] | null}
          functions={functions as APIReferenceFunction[] | null}
          mathOperations={mathOperations as APIReferenceMathOperation[] | null}
        />
      </div>
    );
  }

  return (
    <div className={root}>
      <APIReferenceDescription name={name} description={description} codeSamples={codeSamples} />
      <ReferenceSummary referenceType={ReferenceType.DATATYPE} reference={datatypeReference} />
      {/* Member Details */}
      {hasConstructors && (
        <Fragment>
          <Typography variant='h2' component='h2' id={constructorsID}>
            {translate('Label.Constructors') || 'Constructors'}
          </Typography>
          <DatatypeCharacteristicsIterator rows={constructors} />
        </Fragment>
      )}
      {hasProperties && (
        <Fragment>
          <Typography variant='h2' component='h2' id={propertiesID}>
            {translate('Label.Properties') || 'Properties'}
          </Typography>
          <DatatypeCharacteristicsIterator rows={properties} />
        </Fragment>
      )}
      {hasMethods && (
        <Fragment>
          <Typography variant='h2' component='h2' id={methodsID}>
            {translate('Label.Methods') || 'Methods'}
          </Typography>
          <DatatypeCharacteristicsIterator rows={methods} />
        </Fragment>
      )}
      {hasFunctions && (
        <Fragment>
          <Typography variant='h2' component='h2' id={functionsID}>
            {translate('Label.Functions') || 'Functions'}
          </Typography>
          <DatatypeCharacteristicsIterator rows={functions} />
        </Fragment>
      )}
      {hasMathOperations && (
        <Fragment>
          <Typography variant='h2' component='h2' id={mathOperationsID}>
            {translate('Label.MathOperations') || 'Math Operations'}
          </Typography>
          <DatatypeCharacteristicsIterator rows={mathOperations} />
        </Fragment>
      )}
    </div>
  );
};
export default DatatypeContent;
